import React, { useCallback, useEffect, useState } from 'react';
import ListGrid from '../../../components/ListGrid';
import Pill from '../../../components/Pill';
import { Section } from './panelParts';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { dayAndClock } from '../../../lib/when';
import {
  loadJob, pickUp, putDown, cancelJob, putBack, actionsFor, STATES, DOORS, URGENCY,
} from './orderJobs';
import { lineDetails } from './transferOrders';
import LinePicker from './LinePicker';
import AePicker from './AePicker';
import RunPanel from './RunPanel';
import AeFileSection from './AeFileSection';
import { loadConversions, buildFamilies, tagFor } from './conversions';
import { newestReports, stockAt, austinStock, today } from './morningReports';

/*
  ONE JOB, OPENED, 5 October 2026.

  **What the owner found the day the queue went live**: *"nothing lets me delete
  it, claim it or open it."* This is the opening, and the four things that are
  the queue rather than processing: pick it up, put it down, cancel it, and put
  a cancelled one back.

  **What it deliberately does not have** is anything to do with working a job
  once it is picked up. That is still not designed, on his instruction, so a
  picked-up job says so in words rather than offering a button that pretends.

  **Cancel, never delete**, his answer of 5 October: a cancelled job leaves the
  queue, is kept with who cancelled it, when and why, and can be put back.
*/

/* A read-only field, in the house style: the label OnHand's forms use, then
   the value at reading size. */
function Fact({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="fld-label">{label}</div>
      <div style={{ fontSize: 15, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );
}

/*
  **Four columns, not eight**, after he found the job panel cramped and hard to
  read, 12 October 2026. What the item is, how many, and which of our items it
  is; the stock at the destination and Austin's spare go under it as a line of
  their own rather than as more columns.
*/
const LINE_COLUMNS = [
  { key: 'no',   label: '#',            width: '48px' },
  { key: 'item', label: 'Item',         width: 'minmax(220px, 1.2fr)' },
  { key: 'qty',  label: 'Quantity',     width: '96px', align: 'right' },
  { key: 'cat',  label: 'In our items', width: 'minmax(260px, 1.6fr)' },
];

/*
  **His colours from the workbook**, 4 October 2026: *"red an item we are not
  ordering any more ... yellow a replacement item"*, and either means look at
  the conversion audit. A badge, as the brand draws every status.
*/
function ConversionTag({ tag }) {
  if (!tag) return null;
  const text = tag.kind === 'replaced' ? 'Replaced by ' + tag.finals.join(' or ')
    : tag.kind === 'no_replacement' ? 'Do not use, no replacement'
    : 'Replacement for ' + tag.replaces.join(', ');
  return (
    <span title={'Family: ' + tag.family.join(', ')} style={{ display: 'inline-block', marginTop: 6 }}>
      <Pill tone={tag.kind === 'replacement' ? 'gold' : 'red'}>{text}</Pill>
    </span>
  );
}

/* The brand's colours for a job's state, as a badge (guide 4.4 and 11.4). */
const STATE_TONE = { ready: 'app', needs_you: 'gold', working: 'blue', done: 'app', cancelled: 'ns', reading: 'blue', could_not_read: 'red' };
export function StateBadge({ state }) {
  return <Pill tone={STATE_TONE[state] || 'ns'}>{STATES[state] ? STATES[state].label : state}</Pill>;
}

const caption = { display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 };

/*
  **In the page, not a pop-up**, his word of 11 October 2026, under the list.
  **Drawn on the house panel from 12 October 2026** (the same header, sections
  and actions row as Warehouses and Users & Access), after he said it was
  cramped, hard to read and did not match the rest of OnHand.
*/
function Shell({ title, state, says, danger, footer, onClose, children }) {
  return (
    <section className="card ua-detail" aria-label={title} style={{ marginTop: 16 }}>
      <div className="ua-detail-body">
        <div className="ua-detail-head">
          <div style={{ minWidth: 0 }}>
            <div className="ua-detail-name">{title} {state && <span style={{ marginLeft: 8, verticalAlign: 'middle' }}><StateBadge state={state} /></span>}</div>
            {says && <div className="ua-detail-sub" style={{ fontSize: 14, color: danger ? 'var(--critical-ink)' : undefined }}>{says}</div>}
          </div>
          <button type="button" className="btn btn-quiet" onClick={onClose} aria-label="Close this job">Close</button>
        </div>
        {children}
        {footer && <div className="ua-actions" style={{ justifyContent: 'flex-end' }}>{footer}</div>}
      </div>
    </section>
  );
}

/* The stock line under a job line: the destination's own Stock Status row, and
   Austin's on hand, min level and excess (12 October 2026). */
function stockLine(office, s, austin) {
  const bits = [];
  if (s === 'none') bits.push(office + ': not on the Stock Status');
  else if (s) bits.push(office + ': par ' + s.par + ', on hand ' + s.onHand + ', on order ' + s.onOrder);
  if (austin) bits.push(austin === 'none' ? 'Austin: none on hand' : 'Austin: on hand ' + austin.onHand + ', min level ' + austin.minLevel + ', excess ' + austin.spare);
  return bits.join('. ');
}

/* Which reports the numbers came from, and a warning when they are not today's. */
function StockNote({ stock, hasDestination }) {
  if (stock.problem) return <p style={{ color: 'var(--critical-ink)', fontSize: 14, margin: 0 }}>The stock could not be read just now: {stock.problem}</p>;
  if (!stock.reports) return null;
  const now = today();
  const bits = [['stock_status', 'Stock Status'], ['excess_calc', 'Excess Calc']].map(([k, label]) => {
    const r = stock.reports[k];
    if (!r) return { label, text: 'not loaded', warn: true };
    return { label, text: r.report_day === now ? 'today' : 'from ' + dayAndClock(r.loaded_at) + ', not today', warn: r.report_day !== now };
  });
  const warn = bits.some(b => b.warn) || !hasDestination;
  return (
    <div style={{ fontSize: 14, padding: '10px 14px', borderRadius: 12, background: warn ? 'var(--warning-bg)' : 'var(--info-bg)', color: warn ? 'var(--warning-ink)' : 'var(--info-ink)' }}>
      Stock from {bits.map((b, i) => <span key={b.label}>{i ? ' and ' : ''}the {b.label} {b.text}</span>)}.
      {!hasDestination && ' The destination is not known, so its stock cannot be shown.'}
    </div>
  );
}

export default function JobPanel({ jobId, onClose, onChanged, reportsVersion = 0 }) {
  const { user } = useAuth();
  const toast = useToast();
  const [state, setState] = useState({ job: null, lines: [], people: {}, error: null, loading: true });
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  /* Ordering stage 2: the line he is saying which item it is, or null. */
  const [fixing, setFixing] = useState(null);
  /* The line whose item he is giving an Account Edge number, or null. */
  const [fixingAe, setFixingAe] = useState(null);

  const [details, setDetails] = useState({ ae: new Map(), price: new Map() });
  const [families, setFamilies] = useState(null);
  const [stock, setStock] = useState({ reports: null, at: new Map(), austin: new Map(), problem: null });
  const read = useCallback(async () => {
    const r = await loadJob(jobId);
    setState({ ...r, loading: false });
    if (!r.lines || !r.lines.length) return;
    loadConversions().then(c => setFamilies(c.error ? null : buildFamilies(c.rows)));
    /* Account Edge number and price, read after the job so a slow price lookup
       never holds the job itself off the screen. */
    const d = await lineDetails(r.lines.map(l => l.catalog_item_id));
    if (!d.error) setDetails(d);
    /* Then the stock, off the newest of each morning report. */
    const nr = await newestReports();
    if (nr.error) { setStock(s => ({ ...s, problem: nr.error.message })); return; }
    const ss = nr.reports.stock_status, ec = nr.reports.excess_calc;
    const dest = r.job && r.job.destination ? r.job.destination.name : null;
    const aes = r.lines.map(l => (d.ae && d.ae.get(l.catalog_item_id)) || l.ae_number).filter(Boolean);
    const [a, b] = await Promise.all([
      stockAt(ss && ss.id, dest, r.lines.map(l => l.item_as_given)),
      austinStock(ec && ec.id, aes),
    ]);
    setStock({ reports: nr.reports, at: a.stock, austin: b.austin, problem: (a.error || b.error) ? (a.error || b.error).message : null });
  }, [jobId]);
  useEffect(() => { read(); }, [read, reportsVersion]);

  const { job, lines, people, error, loading } = state;

  if (loading || !job) {
    return (
      <Shell title={loading ? 'Opening...' : 'This job could not be opened'} onClose={onClose}
        footer={!loading && <button className="btn btn-quiet" onClick={() => { setState(s => ({ ...s, loading: true })); read(); }}>Try again</button>}>
        <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          {loading ? 'Reading the job...' : (error && error.message) || 'Nothing came back.'}
        </div>
      </Shell>
    );
  }

  const me = user && user.id;
  const nameOf = id => (id && id === me ? 'you' : people[id] || 'someone');
  const actions = actionsFor(job.state);

  async function act(run, said) {
    setBusy(true);
    const res = await run();
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error?.message || 'That did not save.');
      read();
      onChanged?.();
      return;
    }
    toast.success(job.reference + ' ' + said);
    const wasCancelling = cancelling;
    setCancelling(false);
    setReason('');
    onChanged?.();
    /* In the page, the job stays open after picking up or putting down, now
       showing its new state. A cancelled job leaves the list, so it closes. */
    if (wasCancelling) onClose(); else read();
  }

  const unmatched = lines.filter(l => !l.catalog_item_id).length;
  const noAe = lines.filter(l => l.catalog_item_id && !l.ae_number).length;
  const canFix = job && !['done', 'cancelled'].includes(job.state);
  const fixed = r => {
    setFixing(null);
    setFixingAe(null);
    if (r.added) toast.success('Added to the catalog as ' + r.added);
    toast.success(r.nowReady && r.nowReady.length
      ? 'Saved. ' + r.nowReady.join(', ') + (r.nowReady.length === 1 ? ' is' : ' are') + ' ready now.'
      : 'Saved' + (r.lines > 1 ? ', and filled in on ' + r.lines + ' lines' : ''));
    if (r.warning) toast.error(r.warning);
    read();
    onChanged?.();
  };

  const footer = (fixing || fixingAe) ? null : cancelling ? (
    <>
      <button className="btn btn-quiet" onClick={() => setCancelling(false)} disabled={busy}>Keep the job</button>
      <button className="btn btn-danger" disabled={busy}
        onClick={() => act(() => cancelJob(job.id, me, reason), 'was cancelled. It is kept under Cancelled.')}>
        {busy ? 'Cancelling...' : 'Yes, cancel it'}
      </button>
    </>
  ) : (
    <>
      {job.state === 'needs_you' && (
        <span style={{ marginRight: 'auto', alignSelf: 'center', fontSize: 14, color: 'var(--text-secondary)' }}>
          Fix the lines marked above and it turns Ready. Only a Ready job can be picked up.
        </span>
      )}
      {actions.includes('cancel') && (
        <button className="btn btn-quiet" style={{ marginRight: job.state === 'needs_you' ? 0 : 'auto' }} onClick={() => setCancelling(true)} disabled={busy}>Cancel this job</button>
      )}
      {actions.includes('put_down') && (
        <button className="btn btn-quiet" disabled={busy}
          onClick={() => act(() => putDown(job, lines), 'is back in the queue')}>
          {busy ? 'Putting down...' : 'Put it down'}
        </button>
      )}
      {actions.includes('pick_up') && (
        <button className="btn btn-primary" disabled={busy}
          onClick={() => act(() => pickUp(job.id, me), 'is yours')}>
          {busy ? 'Picking up...' : 'Pick it up'}
        </button>
      )}
      {actions.includes('put_back') && (
        <button className="btn btn-primary" disabled={busy}
          onClick={() => act(() => putBack(job, lines), 'is back in the queue')}>
          {busy ? 'Putting back...' : 'Put it back in the queue'}
        </button>
      )}
    </>
  );

  const office = job.destination ? job.destination.onhand_name : 'Destination';
  const lineNote = [
    unmatched > 0 && unmatched + ' not known to OnHand yet',
    noAe > 0 && noAe + ' with no Account Edge number',
  ].filter(Boolean).join(', ');

  return (
    <Shell
      danger={cancelling}
      title={job.reference}
      state={job.state}
      says={cancelling
        ? 'It leaves the queue and is kept under Cancelled, with your name and the reason. You can put it back.'
        : job.state === 'working'
        ? 'Picked up by ' + nameOf(job.picked_up_by) + (job.picked_up_at ? ', ' + dayAndClock(job.picked_up_at) : '') + '.'
        : job.state === 'cancelled'
        ? 'Cancelled by ' + nameOf(job.cancelled_by) + (job.cancelled_at ? ', ' + dayAndClock(job.cancelled_at) : '') + '.' + (job.cancel_reason ? ' "' + job.cancel_reason + '"' : '')
        : undefined}
      onClose={onClose}
      footer={footer}
    >
      {fixingAe ? (
        <AePicker line={fixingAe} userId={me} onBack={() => setFixingAe(null)} onDone={fixed} />
      ) : fixing ? (
        <LinePicker line={fixing} userId={me} onBack={() => setFixing(null)} onDone={fixed} />
      ) : cancelling ? (
        <label style={{ display: 'block' }}>
          <span className="fld-label">Why? (optional)</span>
          <input className="fld" autoFocus value={reason} maxLength={300}
            placeholder="For example: a duplicate of JOB-00003, or a test"
            onChange={e => setReason(e.target.value)} style={{ width: '100%' }} />
        </label>
      ) : (
        <>
          <div className="ua-fields">
            <Fact label="Going to">
              {job.destination
                ? job.destination.onhand_name
                : <Pill tone="red">Not known yet</Pill>}
            </Fact>
            <Fact label="Came in by">{DOORS[job.door] || job.door}</Fact>
            <Fact label={job.source_reference ? 'Their reference' : 'Who asked'}>{job.source_reference || job.requested_by_name || 'No one named'}</Fact>
            <Fact label="Arrived">{dayAndClock(job.arrived_at)}</Fact>
            {job.urgency && job.urgency !== 'routine' && <Fact label="Urgency">{(URGENCY[job.urgency] || {}).label || job.urgency}</Fact>}
            {job.customer_po && <Fact label="Their PO">{job.customer_po}</Fact>}
          </div>
          {job.note && <Fact label="Note">{job.note}</Fact>}

          {job.state === 'working' ? (
            <RunPanel job={job} lines={lines} userId={me} onSaved={() => { read(); onChanged && onChanged(); }}
              onFinished={() => { onChanged && onChanged(); onClose(); }} />
          ) : (
            <Section title={lines.length + ' line' + (lines.length === 1 ? '' : 's')}
              note={lineNote || (job.state === 'ready' ? 'Every line is one of our items with an Account Edge number.' : undefined)}>
              <StockNote stock={stock} hasDestination={!!(job.destination && job.destination.name)} />
              <ListGrid
                columns={LINE_COLUMNS}
                rows={lines}
                keyOf={l => l.id}
                cells={l => {
                  const aeNum = details.ae.get(l.catalog_item_id) || l.ae_number;
                  const here = stock.reports && stock.reports.stock_status ? (stock.at.get(String(l.item_as_given).trim().toUpperCase()) || 'none') : null;
                  const aeKey = (aeNum || '').trim().toUpperCase();
                  const spare = stock.reports && stock.reports.excess_calc && aeKey ? (stock.austin.get(aeKey) || 'none') : null;
                  return [
                    <span className="rpnum">{l.line_no}</span>,
                    <span>
                      <span className="rpnum" style={{ fontSize: 15, fontWeight: 700 }}>{l.item_as_given}</span>
                      {l.description && <span style={caption}>{l.description}</span>}
                      {families && <ConversionTag tag={tagFor(families, l.item_as_given)} />}
                    </span>,
                    <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Number(l.quantity)}</span>,
                    l.catalog_item_id ? (
                      <span>
                        <span style={{ fontSize: 15 }}>{(l.item && (l.item.our_description || l.item.vendor_description)) || 'Matched'}</span>
                        <span style={caption}>
                          {[l.vendor && l.vendor.name, aeNum ? 'Account Edge ' + aeNum : null].filter(Boolean).join(', ')}
                        </span>
                        {!aeNum && (
                          <span style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                            <Pill tone="red">No Account Edge number</Pill>
                            {canFix && <button type="button" className="btn btn-quiet btn-sm" onClick={() => setFixingAe(l)}>Which Account Edge item?</button>}
                          </span>
                        )}
                        {(here || spare) && <span style={caption}>{stockLine(office, here, spare)}</span>}
                      </span>
                    ) : (
                      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Pill tone="red">Not known to OnHand yet</Pill>
                        {canFix && <button type="button" className="btn btn-quiet btn-sm" onClick={() => setFixing(l)}>Which item is this?</button>}
                      </span>
                    ),
                  ];
                }}
                empty="This job has no lines."
              />
              {/* A finished job keeps its files, to download again (stage 6). */}
              {job.state === 'done' && <AeFileSection job={job} lines={lines} userId={me} finished />}
            </Section>
          )}
        </>
      )}
    </Shell>
  );
}
